import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  Img,
  staticFile,
} from 'remotion';

/**
 * 美术鉴赏 - 横屏「画廊展览」风格（重构版）
 *
 * 核心亮点：
 * - 左侧大面积真实画作图片（通过 imageUrl 属性传入）
 * - 帷幕拉开揭幕动画
 * - 构图网格覆盖（三分法/黄金分割线）
 * - 右侧色彩提取面板 + 技法分析 + 历史地位
 * - 画廊深色展厅背景 + 射灯光效 + 金框
 *
 * 图片使用说明：
 *   将画作图片放到 public/art-gallery/ 目录下，
 *   然后通过 imageUrl 属性传入 staticFile 路径，如：
 *   imageUrl: staticFile('art-gallery/starry-night.jpg')
 *
 * 尺寸：1920x1080 横屏
 */

interface ColorSwatch {
  hex: string;
  name: string;
  percentage: number;
}

interface CompositionLine {
  type: 'thirds' | 'golden' | 'diagonal';
  label: string;
}

interface ArtDetail {
  label: string;
  description: string;
}

interface ArtGalleryTemplateProps {
  paintingTitle: string;
  paintingTitleEn: string;
  artist: string;
  year: string;
  style: string;
  imageUrl: string;
  imageFit: 'cover' | 'contain';
  colors: ColorSwatch[];
  compositionType: CompositionLine;
  details: ArtDetail[];
  significance: string;
  funFact: string;
}

const DEFAULT_PROPS: ArtGalleryTemplateProps = {
  paintingTitle: '星月夜',
  paintingTitleEn: 'The Starry Night',
  artist: '文森特·梵高',
  year: '1889',
  style: '后印象主义',
  imageUrl: staticFile('art-gallery/starry-night.jpg'),
  imageFit: 'cover',
  colors: [
    { hex: '#1a3a5c', name: '普鲁士蓝', percentage: 35 },
    { hex: '#f5c542', name: '铬黄', percentage: 22 },
    { hex: '#2d5a27', name: '翠绿', percentage: 18 },
    { hex: '#f0e6c0', name: '月白', percentage: 15 },
    { hex: '#1a1a2e', name: '暗夜蓝', percentage: 10 },
  ],
  compositionType: { type: 'thirds', label: '三分法构图' },
  details: [
    { label: '笔触', description: '漩涡状厚涂笔触表现夜空的涌动能量' },
    { label: '色彩', description: '冷暖对比：深蓝夜空中点缀金黄星光' },
    { label: '构图', description: '柏树作为前景引导视线，村庄位于下三分之一' },
  ],
  significance: '梵高在精神病院期间创作，将内心的激荡投射到夜空，是表现主义的先驱之作',
  funFact: '这幅画在梵高生前从未售出，如今估价超过 1 亿美元',
};

/* ====================== Main Template ====================== */

export const ArtGalleryTemplate: React.FC<Partial<ArtGalleryTemplateProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const curtainOpen = interpolate(frame, [0, 50], [0, 1], { extrapolateRight: 'clamp' });
  const headerProgress = spring({ frame: frame - 30, fps, config: { damping: 18, stiffness: 90 } });

  const paintingW = 880;
  const paintingH = 680;

  const spotlightIntensity = interpolate(frame, [40, 70], [0, 1], { extrapolateRight: 'clamp' });

  const gridStart = 80;
  const gridDraw = interpolate(frame, [gridStart, gridStart + 40], [0, 1], { extrapolateRight: 'clamp' });

  const colorStart = 130;
  const detailStart = 200;
  const detailInterval = 45;
  const sigStart = detailStart + p.details.length * detailInterval + 20;
  const sigOpacity = interpolate(frame, [sigStart, sigStart + 25], [0, 1], { extrapolateRight: 'clamp' });
  const factStart = sigStart + 40;
  const factOpacity = interpolate(frame, [factStart, factStart + 25], [0, 1], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill
      style={{
        background: '#0a0a0a',
        fontFamily: '-apple-system, "PingFang SC", "Inter", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Gallery wall */}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, #1a1a1a 0%, #0a0a0a 50%, #111 100%)' }} />

      {/* Spotlight cone */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: -120,
          width: paintingW + 200,
          height: 900,
          background: `radial-gradient(ellipse at 50% 0%, rgba(255,245,220,${0.1 * spotlightIntensity}), transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      {/* ========== PAINTING AREA (left ~55%) ========== */}
      <div
        style={{
          position: 'absolute',
          left: 60,
          top: 90,
          width: paintingW + 44,
          height: paintingH + 44,
        }}
      >
        {/* Gold frame */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            border: '10px solid #8b7340',
            borderRadius: 6,
            boxShadow: 'inset 0 0 30px rgba(139,115,64,0.3), 0 12px 50px rgba(0,0,0,0.6)',
            zIndex: 2,
            pointerEvents: 'none',
          }}
        />

        {/* Inner frame bevel */}
        <div
          style={{
            position: 'absolute',
            inset: 10,
            border: '3px solid #6b5a30',
            boxShadow: 'inset 0 0 8px rgba(0,0,0,0.4)',
            zIndex: 2,
            pointerEvents: 'none',
          }}
        />

        {/* Real painting image */}
        <div style={{ position: 'absolute', inset: 13, overflow: 'hidden', background: '#111', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Img
            src={p.imageUrl}
            style={p.imageFit === 'contain' ? {
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
            } : {
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </div>

        {/* Curtain effect */}
        <div style={{ position: 'absolute', inset: 13, display: 'flex', overflow: 'hidden', pointerEvents: 'none', zIndex: 3 }}>
          <div style={{ width: '50%', height: '100%', background: '#1a1a1a', transform: `translateX(${-curtainOpen * 100}%)` }} />
          <div style={{ width: '50%', height: '100%', background: '#1a1a1a', transform: `translateX(${curtainOpen * 100}%)` }} />
        </div>

        {/* Composition grid overlay */}
        {gridDraw > 0 && (
          <svg
            style={{ position: 'absolute', inset: 13, zIndex: 4, pointerEvents: 'none' }}
            width={paintingW + 18}
            height={paintingH + 18}
            viewBox={`0 0 ${paintingW + 18} ${paintingH + 18}`}
          >
            {p.compositionType.type === 'thirds' && (
              <>
                {[1 / 3, 2 / 3].map((t) => (
                  <React.Fragment key={t}>
                    <line x1={t * (paintingW + 18)} y1={0} x2={t * (paintingW + 18)} y2={(paintingH + 18) * gridDraw} stroke="#fbbf24" strokeWidth="1.5" opacity="0.55" strokeDasharray="8 5" />
                    <line x1={0} y1={t * (paintingH + 18)} x2={(paintingW + 18) * gridDraw} y2={t * (paintingH + 18)} stroke="#fbbf24" strokeWidth="1.5" opacity="0.55" strokeDasharray="8 5" />
                  </React.Fragment>
                ))}
                {/* Power points */}
                {[1 / 3, 2 / 3].flatMap((tx) =>
                  [1 / 3, 2 / 3].map((ty) => (
                    <circle
                      key={`${tx}-${ty}`}
                      cx={tx * (paintingW + 18)}
                      cy={ty * (paintingH + 18)}
                      r={6 * gridDraw}
                      fill="none"
                      stroke="#fbbf24"
                      strokeWidth="1.5"
                      opacity={0.6 * gridDraw}
                    />
                  ))
                )}
              </>
            )}
            {p.compositionType.type === 'golden' && (
              <>
                {[0.382, 0.618].map((t) => (
                  <React.Fragment key={t}>
                    <line x1={t * (paintingW + 18)} y1={0} x2={t * (paintingW + 18)} y2={(paintingH + 18) * gridDraw} stroke="#fbbf24" strokeWidth="1.5" opacity="0.55" strokeDasharray="8 5" />
                    <line x1={0} y1={t * (paintingH + 18)} x2={(paintingW + 18) * gridDraw} y2={t * (paintingH + 18)} stroke="#fbbf24" strokeWidth="1.5" opacity="0.55" strokeDasharray="8 5" />
                  </React.Fragment>
                ))}
                {[0.382, 0.618].flatMap((tx) =>
                  [0.382, 0.618].map((ty) => (
                    <circle
                      key={`${tx}-${ty}`}
                      cx={tx * (paintingW + 18)}
                      cy={ty * (paintingH + 18)}
                      r={6 * gridDraw}
                      fill="none"
                      stroke="#fbbf24"
                      strokeWidth="1.5"
                      opacity={0.6 * gridDraw}
                    />
                  ))
                )}
              </>
            )}
          </svg>
        )}

        {/* Grid label */}
        {gridDraw > 0.5 && (
          <div
            style={{
              position: 'absolute',
              bottom: -60,
              left: '50%',
              transform: 'translateX(-50%)',
              padding: '6px 20px',
              background: '#fbbf2420',
              border: '1px solid #fbbf2440',
              borderRadius: 10,
              fontSize: 20,
              color: '#fbbf24',
              opacity: gridDraw,
              whiteSpace: 'nowrap',
              fontWeight: 600,
              zIndex: 5,
            }}
          >
            🎯 {p.compositionType.label}
          </div>
        )}
      </div>

      {/* ========== RIGHT PANEL ========== */}
      <div
        style={{
          position: 'absolute',
          top: 70,
          right: 50,
          width: 820,
        }}
      >
        {/* Header */}
        <div style={{ opacity: headerProgress, transform: `translateY(${(1 - headerProgress) * -12}px)` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
            <div style={{ padding: '6px 20px', background: '#8b734025', border: '1px solid #8b734050', borderRadius: 10, fontSize: 22, color: '#d4a847', fontWeight: 700, letterSpacing: '0.06em' }}>
              {p.style}
            </div>
            <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: 24 }}>{p.year}</span>
          </div>
          <h1 style={{ fontSize: 56, fontWeight: 900, margin: 0, letterSpacing: '-0.01em' }}>{p.paintingTitle}</h1>
          <p style={{ fontSize: 26, color: 'rgba(255,255,255,0.35)', marginTop: 6, fontStyle: 'italic' }}>{p.paintingTitleEn}</p>
          <p style={{ fontSize: 28, color: '#d4a847', marginTop: 12 }}>🎨 {p.artist}</p>
        </div>

        {/* Color palette */}
        <div style={{ marginTop: 32 }}>
          <div style={{ fontSize: 24, color: 'rgba(255,255,255,0.4)', marginBottom: 14, letterSpacing: '0.1em', fontWeight: 600 }}>色彩构成</div>
          <div style={{ display: 'flex', gap: 4, height: 28, borderRadius: 8, overflow: 'hidden', marginBottom: 14 }}>
            {p.colors.map((c, i) => {
              const w = interpolate(frame, [colorStart + i * 10, colorStart + i * 10 + 25], [0, c.percentage], { extrapolateRight: 'clamp' });
              return <div key={i} style={{ width: `${w}%`, height: '100%', background: c.hex }} />;
            })}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
            {p.colors.map((c, i) => {
              const opacity = interpolate(frame, [colorStart + i * 10, colorStart + i * 10 + 15], [0, 1], { extrapolateRight: 'clamp' });
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, opacity }}>
                  <div style={{ width: 16, height: 16, borderRadius: 4, background: c.hex, border: '1px solid rgba(255,255,255,0.12)' }} />
                  <span style={{ fontSize: 22, color: 'rgba(255,255,255,0.55)' }}>{c.name}</span>
                  <span style={{ fontSize: 24, color: 'rgba(255,255,255,0.25)' }}>{c.percentage}%</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Technique analysis */}
        <div style={{ marginTop: 32 }}>
          <div style={{ fontSize: 24, color: 'rgba(255,255,255,0.4)', marginBottom: 14, letterSpacing: '0.1em', fontWeight: 600 }}>技法分析</div>
          {p.details.map((d, i) => {
            const start = detailStart + i * detailInterval;
            const progress = spring({ frame: frame - start, fps, from: 0, to: 1, config: { damping: 14, stiffness: 120, mass: 0.5 } });
            return (
              <div
                key={i}
                style={{
                  padding: '18px 24px',
                  background: 'rgba(255,255,255,0.04)',
                  borderRadius: 14,
                  border: '1px solid rgba(255,255,255,0.07)',
                  marginBottom: 12,
                  opacity: progress,
                  transform: `translateX(${(1 - progress) * 20}px)`,
                }}
              >
                <div style={{ fontSize: 24, fontWeight: 700, color: '#d4a847', marginBottom: 6 }}>🖌 {d.label}</div>
                <div style={{ fontSize: 22, color: 'rgba(255,255,255,0.7)', lineHeight: 1.55 }}>{d.description}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ========== BOTTOM SECTION ========== */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: 60,
          right: 50,
          display: 'flex',
          gap: 24,
        }}
      >
        <div
          style={{
            flex: 3,
            padding: '22px 28px',
            background: 'rgba(212,168,71,0.07)',
            border: '1px solid rgba(212,168,71,0.18)',
            borderRadius: 16,
            opacity: sigOpacity,
          }}
        >
          <div style={{ fontSize: 24, color: '#d4a847', fontWeight: 700, marginBottom: 8, letterSpacing: '0.08em' }}>🏛 艺术史地位</div>
          <div style={{ fontSize: 22, color: 'rgba(255,255,255,0.75)', lineHeight: 1.6 }}>{p.significance}</div>
        </div>

        <div
          style={{
            flex: 2,
            padding: '22px 28px',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 16,
            opacity: factOpacity,
          }}
        >
          <div style={{ fontSize: 24, color: 'rgba(255,255,255,0.45)', fontWeight: 700, marginBottom: 8 }}>💡 冷知识</div>
          <div style={{ fontSize: 22, color: 'rgba(255,255,255,0.65)', lineHeight: 1.6 }}>{p.funFact}</div>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 480], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 3,
          background: 'linear-gradient(90deg, #8b7340, #d4a847)',
        }}
      />
    </AbsoluteFill>
  );
};
